import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { calculateAge } from "../../utils/calculateAge";
import {
  CalendarIcon,
  UserGroupIcon,
  XMarkIcon,
  CreditCardIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { logInfo, logError } from "../../utils/logger";
import {
  fetchTreatingClinician,
  fetchSupervisingClinician,
} from "../../store/store";
import ClientDetails from "./ClientDetails";
import FamilyDetails from "./FamilyDetails";
import ClientInvoices from "./ClientInvoices";
import ClientSoapNotes from "./ClientSoapNotes";
import ClientBlocks from "./ClientBlocks";
import ClientNotes from "./ClientNotes";
import FileUpload from "./FileUpload";
import ClientUploads from "./ClientUploads";
import ClientJournal from "./ClientJournal";
import ClientTreatmentPlans from "./ClientTreatmentPlans";
import { clearSelectedClinicians } from "../../store/slices/cliniciansSlice";
import { clearSelectedClient } from "../../store/slices/clientsSlice";
import ClientAppointments from "./ClientAppointments";
import ClientDeposits from "./ClientDeposits";
import ClientPortal from "./ClientPortal";
import PaymentContractModal from "./PaymentContractModal";
import { updateClient, deleteClient } from "../../store/thunks/clientsThunk";
import ClientJourneyProgressBar from "../../components/ui/ClientJourneyProgressBar";
import ClientJourney from "./ClientJourney";
import { clearCurrentJourney } from "../../store/slices/clientJourneySlice";
import { fetchClientJourneyById } from "../../store/thunks/clientJourneyThunk";
import ClientIntake from "./ClientIntake";
import DeleteClientModal from "./DeleteClientModal";
import { fetchClientTreatmentPlans } from "../../store/thunks/treatmentPlanThunk";

export default function ClientOverview({
  isModal = false,
  onAppointmentClick,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState("details");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [shouldOpenInvoiceModal, setShouldOpenInvoiceModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const treatingClinician = useSelector(
    (state) => state.clinicians.selectedTreatingClinician
  );
  const supervisingClinician = useSelector(
    (state) => state.clinicians.selectedSupervisingClinician
  );
  const client = useSelector((state) => state.clients.selectedClient);
  const { currentJourney, status } = useSelector(
    (state) => state.clientJourney || { currentJourney: null, status: "idle" }
  );
  const clickedItem = useSelector((state) => state.appState.clickedItem);
  const { clientTreatmentPlans } = useSelector((state) => state.treatmentPlan);

  // Handle navigation from search results and location state
  useEffect(() => {
    const targetTab = location.state?.selectedTab || clickedItem?.selectedTab;

    console.log("ClientOverview navigation effect triggered", {
      targetTab,
      locationTab: location.state?.selectedTab,
      clickedItemTab: clickedItem?.selectedTab,
      currentTab: selectedTab,
      clickedItem: clickedItem,
    });

    if (targetTab) {
      logInfo("Setting tab from navigation", {
        selectedTab: targetTab,
        source: location.state?.selectedTab ? "location" : "clickedItem",
        hasSelectedInvoice: !!clickedItem?.selectedInvoice,
      });
      setSelectedTab(targetTab);

      // If there's a selected invoice, set flag to open modal after tab change
      if (clickedItem?.selectedInvoice) {
        setShouldOpenInvoiceModal(true);
      }
    }
  }, [
    location.state?.selectedTab,
    clickedItem?.selectedTab,
    clickedItem?.selectedInvoice,
    selectedTab,
  ]);

  useEffect(() => {
    if (client?.treatingClinician) {
      const treatingClinicianId =
        client.treatingClinician.current?._id || client.treatingClinician._id;
      if (treatingClinicianId) {
        dispatch(fetchTreatingClinician(treatingClinicianId));
      }
    }

    if (client?.supervisingClinician) {
      const supervisingClinicianId =
        client.supervisingClinician.current?._id ||
        client.supervisingClinician._id;
      if (supervisingClinicianId) {
        dispatch(fetchSupervisingClinician(supervisingClinicianId));
      }
    }

    // Only fetch journey if we have a client ID
    if (client?._id) {
      dispatch(fetchClientJourneyById(client._id));
      // Fetch treatment plans to check if any exist
      dispatch(fetchClientTreatmentPlans({ clientId: client._id, page: 1, limit: 1 }));
    }
  }, [client?._id, dispatch]);

  // Separate useEffect for cleanup only on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSelectedClinicians());
      dispatch(clearSelectedClient());
      dispatch(clearCurrentJourney());
    };
  }, []); // Empty dependency array means this only runs on unmount

  const clientAge = client?.dateOfBirth
    ? calculateAge(client.dateOfBirth)
    : "N/A";

  // Helper function to get client status display
  const getClientStatusDisplay = (status) => {
    const statusMap = {
      active: {
        color: "bg-green-100 text-green-800 border-green-200",
        icon: CheckCircleIcon,
        label: "Active",
      },
      inactive: {
        color: "bg-gray-100 text-gray-800 border-gray-200",
        icon: ExclamationCircleIcon,
        label: "Inactive",
      },
      discharged: {
        color: "bg-blue-100 text-blue-800 border-blue-200",
        icon: CheckCircleIcon,
        label: "Discharged",
      },
      recall: {
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: ClockIcon,
        label: "Recall",
      },
      deceased: {
        color: "bg-red-100 text-red-800 border-red-200",
        icon: XMarkIcon,
        label: "Deceased",
      },
      other: {
        color: "bg-purple-100 text-purple-800 border-purple-200",
        icon: ExclamationCircleIcon,
        label: "Other",
      },
    };
    return statusMap[status?.toLowerCase()] || statusMap.active;
  };

  // Helper function to format address for letter format
  const getFormattedAddress = () => {
    if (!client?.address) return null;
    const { aptUnit, streetNumber, streetName, city, province, postalCode } =
      client.address;
    const addressLines = [];

    // Street address line
    if (aptUnit || streetNumber || streetName) {
      const streetParts = [];
      if (aptUnit) streetParts.push(aptUnit);
      if (streetNumber) streetParts.push(streetNumber);
      if (streetName) streetParts.push(streetName);
      if (streetParts.length > 0) addressLines.push(streetParts.join(" "));
    }

    // City, Province PostalCode line
    if (city || province || postalCode) {
      const cityLineParts = [];
      if (city) cityLineParts.push(city);
      if (province) cityLineParts.push(province);
      if (postalCode) cityLineParts.push(postalCode);
      if (cityLineParts.length > 0) addressLines.push(cityLineParts.join(", "));
    }

    return addressLines.length > 0 ? addressLines : null;
  };

  // Helper function to get primary phone
  const getPrimaryPhone = () => {
    if (!client?.phoneNumbers || client.phoneNumbers.length === 0) return null;
    const mobilePhone = client.phoneNumbers.find((phone) => phone.isMobile);
    return mobilePhone || client.phoneNumbers[0];
  };

  const statusDisplay = getClientStatusDisplay(client?.clientStatus);
  const StatusIcon = statusDisplay.icon;
  const formattedAddress = getFormattedAddress();
  const primaryPhone = getPrimaryPhone();

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    logInfo("Tab changed", { newTab: tab });
  };

  const handleCopyEmail = () => {
    if (client?.email) {
      navigator.clipboard.writeText(client.email);
      toast.success("Copied to clipboard!");
      logInfo("Client email copied to clipboard", {
        email: client.email,
      });
    }
  };

  const handlePaymentInfoClick = () => {
    setIsPaymentModalOpen(true);
  };

  const handlePaymentInfoSave = async (formData) => {
    try {
      await dispatch(
        updateClient({
          id: client._id,
          clientData: {
            contract: formData.contract,
            defaultPaymentMethod: formData.defaultPaymentMethod,
            creditCardOnFile: formData.creditCardOnFile,
            splitBilling: formData.splitBilling,
          },
        })
      ).unwrap();

      setSuccessMessage(
        `Payment information updated successfully for ${client.firstName} ${client.lastName}`
      );
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2000);
    } catch (error) {
      toast.error("Failed to update payment information");
      logError("Failed to update payment information", { error });
    }
  };

  const handleDeleteClient = async () => {
    try {
      setIsDeleting(true);
      console.log(
        `[Client Overview] Starting deletion for client: ${client.firstName} ${client.lastName}`
      );

      await dispatch(deleteClient(client._id)).unwrap();

      toast.success(
        `Client ${client.firstName} ${client.lastName} has been deleted successfully`
      );
      console.log(
        `[Client Overview] Client archived successfully: ${client._id}`
      );

      // Clear the selected client and close the modal
      dispatch(clearSelectedClient());
      dispatch(clearCurrentJourney());
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error(`[Client Overview] Failed to delete client:`, error);
      toast.error(
        `Failed to delete client: ${error.message || "Unknown error"}`
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (!client) {
    return (
      <div className="w-full h-40 flex items-center justify-center">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-200 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Enhanced Header Section with Blue Background */}
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-gray-200">
        {/* Client Journey Progress Bar */}
        {client?._id && (
          <div className="pt-4 pb-2 px-6">
            <ClientJourneyProgressBar clientId={client._id} />
          </div>
        )}

        {/* Main Header Content */}
        <div className="p-6 relative">
          <div className="flex flex-col 2xl:flex-row 2xl:items-start 2xl:justify-between gap-6">
            {/* Client Profile Section */}
            <div className="flex items-start space-x-6 flex-1 min-w-0">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold shadow-lg ring-4 ring-white">
                  {client?.firstName?.[0]?.toUpperCase()}
                  {client?.lastName?.[0]?.toUpperCase()}
                </div>
              </div>

              {/* Client Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3 flex-wrap">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {client?.firstName} {client?.lastName}
                    </h2>
                    {clientAge !== "N/A" && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {clientAge} years old
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${statusDisplay.color}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusDisplay.label}
                    </span>

                    {/* Family Link Indicators */}
                    {client?.parents && client.parents.length > 0 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                        <UserGroupIcon className="h-3 w-3 mr-1" />
                        {client.parents.length} Parent
                        {client.parents.length !== 1 ? "s" : ""}
                      </span>
                    )}

                    {client?.children && client.children.length > 0 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                        <UserGroupIcon className="h-3 w-3 mr-1" />
                        {client.children.length} Child
                        {client.children.length !== 1 ? "ren" : ""}
                      </span>
                    )}
                  </div>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                  {/* Left Column - Contact Information */}
                  <div className="space-y-3">
                    {/* Address Container */}
                    {formattedAddress && (
                      <div className="flex items-start space-x-2 p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                        <MapPinIcon className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">
                            Address
                          </p>
                          <div className="text-sm font-semibold text-gray-900">
                            {formattedAddress.map((line, index) => (
                              <div key={index}>{line}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Phone Container */}
                    {primaryPhone && (
                      <div className="flex items-center space-x-2 p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                        <PhoneIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">
                            {primaryPhone.type || "Phone"}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {primaryPhone.number}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Email Container */}
                    {client?.email && (
                      <div
                        className="flex items-center space-x-2 p-3 bg-white rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors"
                        onClick={handleCopyEmail}>
                        <EnvelopeIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">
                            Email (click to copy)
                          </p>
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {client.email}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Action Cards */}
                  <div>
                    {/* Appointment & Clinician Info Card */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-500" />
                        Care Team & Appointments
                      </h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">
                            Next Appointment:
                          </span>
                          <span className="font-medium text-gray-900 text-right">
                            {client?.appointments?.[0]?.start
                              ? new Intl.DateTimeFormat("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                                }).format(
                                  new Date(client.appointments[0].start)
                                )
                              : "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Treating:</span>
                          <span className="font-medium text-gray-900">
                            {treatingClinician?.firstName}{" "}
                            {treatingClinician?.lastName || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Supervising:</span>
                          <span className="font-medium text-gray-900">
                            {supervisingClinician?.firstName}{" "}
                            {supervisingClinician?.lastName || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Treatment Plan:</span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              clientTreatmentPlans?.treatmentPlans?.docs?.length > 0
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}>
                            {clientTreatmentPlans?.treatmentPlans?.docs?.length > 0 ? "YES" : "NO"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Payment & Contract Info Card */}
                    <div
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-all hover:border-blue-200 mt-4"
                      onClick={handlePaymentInfoClick}>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <CreditCardIcon className="h-4 w-4 mr-2 text-gray-500" />
                        Payment & Contract
                        <span className="ml-auto text-xs text-gray-400">
                          Click to edit
                        </span>
                      </h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">
                            Contract Signed:
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              client?.contract?.signed
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}>
                            {client?.contract?.signed ? "Yes" : "No"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Payment Method:</span>
                          <span className="font-medium text-gray-900">
                            {client?.defaultPaymentMethod?.name || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Card on File:</span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              client?.creditCardOnFile
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}>
                            {client?.creditCardOnFile ? "Yes" : "No"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Split Billing:</span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              client?.splitBilling?.enabled
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800"
                            }`}>
                            {client?.splitBilling?.enabled ? "Yes" : "No"}
                          </span>
                        </div>
                        {client?.splitBilling?.instructions && (
                          <div className="pt-2 border-t border-gray-100">
                            <span className="text-xs text-gray-500 block mb-1">
                              Instructions:
                            </span>
                            <p className="text-xs text-gray-700 bg-gray-50 p-2 rounded border border-gray-100 whitespace-pre-wrap">
                              {client.splitBilling.instructions}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons - Fixed position in top-right */}
            <div className="absolute top-4 right-4 flex items-center space-x-2">
              {/* Delete Button */}
              <button
                onClick={() => {
                  console.log(
                    `[Client Overview] Delete button clicked for client: ${client?.firstName} ${client?.lastName}`
                  );
                  setIsDeleteModalOpen(true);
                }}
                className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                title="Delete Client">
                <TrashIcon className="h-4 w-4 text-red-400 group-hover:text-red-500" />
              </button>

              {/* Close Button */}
              <button
                onClick={() => {
                  logInfo("Client overview closed", { clientId: client?._id });
                  dispatch(clearSelectedClient());
                  dispatch(clearCurrentJourney());
                }}
                className="p-2 hover:bg-white hover:bg-opacity-80 rounded-lg transition-colors">
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
        <ToastContainer position="bottom-right" autoClose={2000} />
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200">
        <nav
          className="flex overflow-x-auto px-4 scrollbar-hide"
          aria-label="Tabs">
          {[
            { id: "details", label: "Client Details" },
            { id: "family", label: "Family Details" },
            { id: "intake", label: "Intake" },
            { id: "invoices", label: "Invoices" },
            { id: "deposits", label: "Deposits" },
            { id: "appointments", label: "Appointments" },
            { id: "blocks", label: "Blocks" },
            { id: "soapNotes", label: "SOAP Notes" },
            { id: "upload", label: "Upload" },
            { id: "files", label: "Files" },
            { id: "txPlans", label: "Tx Plans" },
            { id: "notes", label: "Notes" },
            { id: "journal", label: "Journal" },
            { id: "portal", label: "Portal" },
            { id: "journey", label: "Journey" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={classNames(
                "px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                selectedTab === tab.id
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              )}
              aria-current={selectedTab === tab.id ? "page" : undefined}>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="p-4">
        {selectedTab === "details" && <ClientDetails />}
        {selectedTab === "family" && <FamilyDetails />}
        {selectedTab === "intake" && <ClientIntake />}
        {selectedTab === "invoices" && (
          <ClientInvoices
            shouldOpenModal={shouldOpenInvoiceModal}
            selectedInvoice={clickedItem?.selectedInvoice}
            onModalOpened={() => setShouldOpenInvoiceModal(false)}
          />
        )}
        {selectedTab === "deposits" && <ClientDeposits />}
        {selectedTab === "appointments" && (
          <ClientAppointments onAppointmentClick={onAppointmentClick} />
        )}
        {selectedTab === "blocks" && <ClientBlocks />}
        {selectedTab === "soapNotes" && <ClientSoapNotes />}
        {selectedTab === "upload" && <FileUpload />}
        {selectedTab === "files" && <ClientUploads client={client} />}
        {selectedTab === "txPlans" && <ClientTreatmentPlans clientId={client?._id} />}
        {selectedTab === "notes" && <ClientNotes client={client} />}
        {selectedTab === "journal" && <ClientJournal client={client} />}
        {selectedTab === "portal" && <ClientPortal client={client} />}
        {selectedTab === "journey" && (
          <ClientJourney clientId={client?._id} clinicId={client?.clinicId} />
        )}
      </div>

      {/* Add the modal */}
      <PaymentContractModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        client={client}
        onSave={handlePaymentInfoSave}
      />

      {/* Delete Client Modal */}
      <DeleteClientModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        client={client}
        onConfirmDelete={handleDeleteClient}
        isDeleting={isDeleting}
      />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for dynamic class names
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
